import { ComponentType, lazy } from 'react';

/**
 * Wraps React.lazy() with automatic retry logic for chunk load errors.
 *
 * When a dynamic import fails (typically due to deployment of new chunks),
 * this will:
 * 1. Retry the import once
 * 2. If still fails, reload the page to get the latest version
 *
 * This prevents the "Failed to fetch dynamically imported module" error
 * that users see when they have an old version loaded during deployment.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    );

    try {
      const component = await componentImport();

      // Reset the flag on successful load
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');

      return component;
    } catch (error) {
      console.error('Chunk load error:', error);

      if (!pageHasAlreadyBeenForceRefreshed) {
        // Mark that we're going to refresh
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');

        console.log('Reloading page to fetch latest chunks...');

        // Reload the page to get the latest version
        window.location.reload();

        // Return a never-resolving promise to prevent further execution
        return new Promise(() => {});
      }

      // If we've already tried refreshing, throw the error
      throw error;
    }
  });
}
